// Merged exams StatCard (JSX value, optional icon, class-based tone chips) + libms StatCard (trend arrows, onClick); tones renamed to semantic daisyUI colors (blue→info, green→success, amber→warning, red→error, purple→secondary, gray→neutral), inline hex dropped; clickable a11y gap fixed (role/tabindex/keydown/focus ring).
//
// FILLED, NEVER OUTLINED (foundations spec §12): `filled` tints the WHOLE tile
// in the tone — the opaque role tint fills the surface (flat, borderless: Spectrum
// two-tone) — so a warning or error KPI reads as a status surface rather than
// "a coloured number on a white card". The label/value stay base-content on the
// tint: a role-inked numeral fails contrast on the soft tint in light mode, and
// the surface already carries the signal.
// Default (unfilled) is unchanged, so neutral metadata tiles are untouched.
import { Show, type JSX } from 'solid-js';
import { TrendingDown, TrendingUp } from 'lucide-solid';
import { huePair, type CategoryHue } from '../category-hue/index.js';

/** The nine categorization hues — identity (module, category), never status.
 *  An alias of `CategoryHue` from @erp/ui/category-hue, kept so existing
 *  `tone` call sites and the strip's ink table read one name. */
export type StatCategoryTone = CategoryHue;

export type StatTone =
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'secondary'
  | 'neutral'
  | StatCategoryTone;

interface Surface {
  wrap: string;
  label: string;
  value: string;
  hint: string;
  chip: string;
}

/* The unfilled tile every tone shares; only `chip` differs per tone. */
const UNFILLED: Omit<Surface, 'chip'> = {
  wrap: 'border border-base-300 bg-base-100',
  label: 'text-base-subtle',
  value: 'text-base-content',
  hint: 'text-base-subtle',
};

/* Complete static class strings per tone — never assembled from fragments. */
const PLAIN: Record<StatTone, Surface> = {
  info: {
    wrap: 'border border-base-300 bg-base-100',
    label: 'text-base-subtle',
    value: 'text-base-content',
    hint: 'text-base-subtle',
    chip: 'bg-info-tint text-info',
  },
  success: {
    wrap: 'border border-base-300 bg-base-100',
    label: 'text-base-subtle',
    value: 'text-base-content',
    hint: 'text-base-subtle',
    chip: 'bg-success-tint text-success',
  },
  warning: {
    wrap: 'border border-base-300 bg-base-100',
    label: 'text-base-subtle',
    value: 'text-base-content',
    hint: 'text-base-subtle',
    chip: 'bg-warning-tint text-warning',
  },
  error: {
    wrap: 'border border-base-300 bg-base-100',
    label: 'text-base-subtle',
    value: 'text-base-content',
    hint: 'text-base-subtle',
    chip: 'bg-error-tint text-error',
  },
  secondary: {
    wrap: 'border border-base-300 bg-base-100',
    label: 'text-base-subtle',
    value: 'text-base-content',
    hint: 'text-base-subtle',
    /* no minted secondary tint — Spectrum says gray tokens, not alpha */
    chip: 'bg-base-300 text-secondary',
  },
  neutral: {
    wrap: 'border border-base-300 bg-base-100',
    label: 'text-base-subtle',
    value: 'text-base-content',
    hint: 'text-base-subtle',
    chip: 'bg-base-200 text-base-muted',
  },
  /* Categorization hues (identity chips — module identity map, category KPIs).
     Same base tile as every unfilled tone; only the chip carries the hue — the
     kit's one PAIR table (@erp/ui/category-hue), no longer a copy of it. */
  indigo: { ...UNFILLED, chip: huePair('indigo') },
  purple: { ...UNFILLED, chip: huePair('purple') },
  fuchsia: { ...UNFILLED, chip: huePair('fuchsia') },
  magenta: { ...UNFILLED, chip: huePair('magenta') },
  yellow: { ...UNFILLED, chip: huePair('yellow') },
  celery: { ...UNFILLED, chip: huePair('celery') },
  chartreuse: { ...UNFILLED, chip: huePair('chartreuse') },
  cyan: { ...UNFILLED, chip: huePair('cyan') },
  seafoam: { ...UNFILLED, chip: huePair('seafoam') },
};

/* Filled = Spectrum two-tone: opaque role tint fills the tile, FLAT (no border — two-tone
   surfaces carry no frame).

   THE CHIP, corrected 2026-09-02 (a same-tint tint-on-tint chip was reported invisible on a
   filled tile — the earlier "bare ink, no edge to draw" reasoning turned out to leave the
   chip genuinely unreadable, not just borderless: measured, its own tint-on-tint contrast is
   ~1.15:1). It now gets a SOLID capsule instead, one step further up the same role's own
   ramp — the fill's 900 stop, paired with that role's already-correct *-content colour
   (daisyUI's own generated pairing, so no new token). Measured against the tile's tint:
   4.6–4.7:1 light, 6.1–6.9:1 darkest, matching the two-tone contrast law elsewhere.

   THE HINT on a filled tile is text-base-muted, not -subtle (2026-09-16, DEV-GUIDE-002
   §12.2): gray-600 is icon/meta duty and as text is allowed only on base-100/200 — on a
   tint it measured 4.47:1 (the audit's "Gray band inks" finding), under the 4.5 floor;
   gray-700 reads ≥ 8.4 on every tint and on the secondary tile's base-300 band. */
const FILLED: Record<StatTone, Surface> = {
  info: {
    wrap: 'bg-info-tint',
    label: 'text-base-muted',
    value: 'text-base-content',
    hint: 'text-base-muted',
    chip: 'bg-info text-info-content',
  },
  success: {
    wrap: 'bg-success-tint',
    label: 'text-base-muted',
    value: 'text-base-content',
    hint: 'text-base-muted',
    chip: 'bg-success text-success-content',
  },
  warning: {
    wrap: 'bg-warning-tint',
    label: 'text-base-muted',
    value: 'text-base-content',
    hint: 'text-base-muted',
    /* Warning is the one role split across two stops (the notice-role text
       duty, DEV-GUIDE-002 §12.4): its FILL (orange-600) sits only 2.19:1
       from warning-tint in light — visibly weaker than the other three — so
       the chip uses --color-warning-text (orange-900, already minted for
       exactly this "the readable stop" duty) instead, landing at 4.64:1.
       Its content is the theme's, not a hand-switch: base.css mints
       --color-warning-text-content (white in light, 5.38:1 on the dark
       orange; black on darkest, where the text stop IS the fill, 8.62:1).
       This was `text-white dark:text-black` — the right colours, chosen here
       per theme, which is the kind of decision the substrate owns; and the
       `dark:` variant was dead in every portal until 2026-09-16 (audit
       COL-02), so the chip's black glyph on darkest only ever rendered in the
       catalog. One token, both themes, no variant to forget. */
    chip: 'bg-(--color-warning-text) text-(--color-warning-text-content)',
  },
  error: {
    wrap: 'bg-error-tint',
    label: 'text-base-muted',
    value: 'text-base-content',
    hint: 'text-base-muted',
    chip: 'bg-error text-error-content',
  },
  secondary: {
    /* no minted secondary tint — Spectrum says gray tokens, not alpha */
    wrap: 'bg-base-300',
    label: 'text-base-muted',
    value: 'text-base-content',
    hint: 'text-base-muted',
    chip: 'bg-secondary text-secondary-content',
  },
  neutral: {
    wrap: 'border border-base-300 bg-base-200',
    label: 'text-base-muted',
    value: 'text-base-content',
    hint: 'text-base-muted',
    chip: 'bg-base-300 text-base-muted',
  },
  /* Categorization hues NEVER fill: a full-tile hue wash is the color law's
     banned "large wash", and filled is the status treatment — these tones
     carry no status. `filled` degrades to the unfilled chip surface. */
  indigo: PLAIN.indigo,
  purple: PLAIN.purple,
  fuchsia: PLAIN.fuchsia,
  magenta: PLAIN.magenta,
  yellow: PLAIN.yellow,
  celery: PLAIN.celery,
  chartreuse: PLAIN.chartreuse,
  cyan: PLAIN.cyan,
  seafoam: PLAIN.seafoam,
};

/* Keyboard focus needs nothing here (2026-09-16). An interactive tile is a
   `role="button"` with tabIndex 0, which the global rule in @erp/ui/tokens.css
   rings on :focus-visible — Spectrum's 2px blue-800 stroke at a 2px gap, the
   ONE colour every focusable shares (DEV-GUIDE-002 §12.3). This file used to
   carry its own three-utility FOCUS_RING constant for exactly that; the admin
   home tiles had copied it; the rule made both redundant, and a ring a call
   site has to remember is a ring some call site forgets. */
const INTERACTIVE = 'cursor-pointer transition-colors hover:bg-(--ui-state-hover)';
/* A tinted tile must not be REPAINTED on hover (a bg- utility would replace the
   tint), and it must not be FADED either — a hover opacity of 80 % composited the
   tint against the page, a colour function on a stop (DEV-GUIDE-002 §12.6:
   transparency is the scrim, the shadow, the state overlays and skeleton
   shimmer, nothing else). So the hover is the same state overlay every table
   row and menu item uses — --ui-state-hover, gray-900 at 0.07, Adobe's table
   hover token — laid over the tint by a ::before that sits below the tile's
   content (isolate + -z-10) and above its background. */
const INTERACTIVE_FILLED =
  'relative isolate cursor-pointer before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:rounded-500 before:transition-colors hover:before:bg-(--ui-state-hover)';

/**
 * One KPI tile: a label, a large value, an optional icon chip, hint and trend.
 * Rows of these sit at the top of an overview or list screen (see
 * `@erp/ui/list-view`'s StatRow). A number that describes only the current
 * PAGE of a list is not a KPI — put it in the pager's footer line instead.
 */
export function StatCard(props: {
  /**
   * What is being counted, in sentence case ("Overdue loans"). WRAPS rather
   * than truncates (audit UX-11, 2026-09-19: rows of five and six tiles cut
   * "Returned this m…" and "Receipted…" mid-word) — a KPI whose name is cut
   * off is a number with no meaning. Rows of these cap at four per row.
   */
  label: string;
  /** The figure. Usually formatted text (`formatINR`, a tabular count); any element is allowed for a unit suffix or a badge. */
  value: JSX.Element;
  /** A lucide icon ELEMENT (`<BookOpen size={18} />`) shown in the chip on the right; decorative, hidden from AT. */
  icon?: JSX.Element;
  /**
   * Semantic tones (info/success/warning/error/secondary/neutral) colour the
   * chip — and the whole tile when `filled`. The nine categorisation hues mark
   * IDENTITY (which module, which category) and never fill. Default neutral.
   */
  tone?: StatTone;
  /** Tint the whole tile in the tone — for warning/error KPIs and other states that must be seen. */
  filled?: boolean;
  /** A movement since the previous period: `dir` picks the arrow and success/error ink; `value` is the delta text ("+12%"). */
  trend?: { dir: 'up' | 'down'; value: string };
  /** One short line under the value — the period, the denominator, the caveat ("this month", "of 1,204"). */
  hint?: string;
  /**
   * Makes the tile a button (role, tabindex, Enter/Space, focus ring). Use it
   * to jump to the filtered list the number came from; leave it off for a
   * figure with no drill-down, so the hover state does not promise one.
   */
  onClick?: () => void;
  /** Automation id on the tile. */
  id?: string;
  /** Extra classes on the tile — grid placement (`col-span-2`), never colour. */
  class?: string;
}) {
  const interactive = () => Boolean(props.onClick);
  const s = () => (props.filled ? FILLED : PLAIN)[props.tone ?? 'neutral'];
  return (
    <div
      id={props.id}
      role={interactive() ? 'button' : undefined}
      tabIndex={interactive() ? 0 : undefined}
      onClick={() => props.onClick?.()}
      onKeyDown={(e) => {
        if (interactive() && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          props.onClick?.();
        }
      }}
      /* 16px AT EVERY WIDTH (2026-10-02, the owner: match the tile). It was
         p-3/p-5 — 12 on a phone, 20 from `sm` — against CardTile's flat 16,
         which is also what Adobe's card measures. One padding means a stat tile
         and a module tile sit on the same page without one looking roomier
         than the other. */
      class={`rounded-500 p-4 ${s().wrap} ${
        interactive() ? (props.filled ? INTERACTIVE_FILLED : INTERACTIVE) : ''
      } ${props.class ?? ''}`}
    >
      {/* THE CHIP RIDES WITH THE LABEL, not with the middle of the copy
          (2026-10-02, measured on the titles register at 1440: the four chips
          in one row sat at y=414/423/431/423 because `items-center` centred
          each one against its own hint, which runs one to three lines). Top
          aligned they line up across the row, and the chip sits beside the
          label exactly as CardTile's capsule sits beside its title. */}
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          {/* ONE TYPE AT EVERY WIDTH (2026-10-02, with the padding above). The
              label was 12 -> 14 and the figure 20 -> 22 across `sm`, so the
              same tile carried two type pairs; it now reads 14 (font-size-100,
              Spectrum's default size) over 22 (font-size-500 =
              heading-size-m), which is the pair the tile was already wearing on
              a desktop. Cost on a phone, measured honestly: 2px on each of two
              lines per tile — against the 539px the hint below used to spend,
              which is why THAT one still stands down. */}
          <div class={`text-sm font-medium leading-snug ${s().label}`}>{props.label}</div>
          <div class={`mt-1 text-2xl font-bold ${s().value}`}>{props.value}</div>
          {/* THE HINT STANDS DOWN ON A PHONE. It is a whole sentence — "Copies
              on the shelf right now — click to show only available titles" —
              and across four tiles it was most of the 539px these cost at
              390px. The tile stays tappable and keeps its label, figure, hue
              and icon; the sentence returns from `sm`, where there is room for
              it. */}
          <Show when={props.hint}>
            <div class={`mt-0.5 hidden text-xs sm:block ${s().hint}`}>{props.hint}</div>
          </Show>
          <Show when={props.trend}>
            {(t) => (
              <span
                class={`mt-1 inline-flex items-center gap-0.5 text-xs font-bold ${
                  t().dir === 'up' ? 'text-success' : 'text-error'
                }`}
              >
                <span class="flex" aria-hidden="true">
                  <Show when={t().dir === 'up'} fallback={<TrendingDown size={12} />}>
                    <TrendingUp size={12} />
                  </Show>
                </span>
                {t().value}
              </span>
            )}
          </Show>
        </div>
        <Show when={props.icon}>
          {/* THE CHIP IS THE IDENTITY CAPSULE'S BOX (2026-10-02): size-8 +
              rounded-field, exactly what IconCapsule size="s" draws (32px at
              corner-radius-500), so a stat tile's chip and a module tile's
              capsule are the same object. It was `h-11 w-11 rounded-700` — a 44px
              box at the POPOVER radius, which is that step's real duty
              (filter-select, searchable-select); at 32px the field step is the
              one Spectrum pairs. A 20px glyph in a 32px box is Adobe's own
              ActionButton M geometry, so no call site changes. */}
          <div
            class={`grid size-8 shrink-0 place-items-center rounded-field ${s().chip}`}
            aria-hidden="true"
          >
            {props.icon}
          </div>
        </Show>
      </div>
    </div>
  );
}
